import { useEffect, useLayoutEffect, useRef, useState, type FC, type ReactNode } from "react";
import "../styles/ContextMenu.css";

export interface ContextMenuItem {
    /** Unique key for the item. */
    key: string;
    /** Content rendered for the item. */
    label: ReactNode;
    /** Invoked when the item is clicked (ignored when disabled). */
    onClick?: () => void;
    /** Renders the item as non-interactive. */
    disabled?: boolean;
    /** Tooltip shown on hover. */
    title?: string;
    /** Items revealed when this item is clicked. */
    children?: ContextMenuItem[];
}

export interface ContextMenuProps {
    /** Whether the menu is currently shown. */
    visible: boolean;
    /** Viewport x coordinate for the menu. */
    x: number;
    /** Viewport y coordinate for the menu. */
    y: number;
    /** Items to render in the menu. */
    items: ContextMenuItem[];
    /** Called when the menu should close (e.g. outside click). */
    onClose: () => void;
}

const ContextMenu: FC<ContextMenuProps> = ({ visible, x, y, items, onClose }) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const submenuRef = useRef<HTMLDivElement>(null);
    const [expandedKey, setExpandedKey] = useState<string | null>(null);

    useEffect(() => {
        setExpandedKey(null);
    }, [visible, x, y]);

    useEffect(() => {
        if (!visible) {
            return;
        }
        const handleClick = () => onClose();
        document.addEventListener("click", handleClick);
        return () => document.removeEventListener("click", handleClick);
    }, [visible, onClose]);

    useLayoutEffect(() => {
        const menu = menuRef.current;
        if (!menu) {
            return;
        }
        const viewportPadding = 8;
        const { width, height } = menu.getBoundingClientRect();
        const left = Math.max(viewportPadding, Math.min(x, window.innerWidth - width - viewportPadding));
        const top = Math.max(viewportPadding, Math.min(y, window.innerHeight - height - viewportPadding));

        menu.style.setProperty("--context-menu-left", `${left}px`);
        menu.style.setProperty("--context-menu-top", `${top}px`);

        const submenu = submenuRef.current;
        const parentItem = menu.querySelector<HTMLElement>("[aria-expanded='true']");
        if (!submenu || !parentItem) {
            return;
        }
        const itemRect = parentItem.getBoundingClientRect();
        const menuRect = menu.getBoundingClientRect();
        const { width: subWidth, height: subHeight } = submenu.getBoundingClientRect();

        // Prefer opening to the right, then the left, otherwise clamp inside the viewport.
        let subLeft = menuRect.right;
        if (subLeft + subWidth > window.innerWidth - viewportPadding) {
            subLeft = menuRect.left - subWidth;
            if (subLeft < viewportPadding) {
                subLeft = Math.max(viewportPadding, window.innerWidth - subWidth - viewportPadding);
            }
        }
        const subTop = Math.max(viewportPadding, Math.min(itemRect.top, window.innerHeight - subHeight - viewportPadding));

        submenu.style.setProperty("--context-submenu-left", `${subLeft}px`);
        submenu.style.setProperty("--context-submenu-top", `${subTop}px`);
    }, [x, y, visible, expandedKey]);

    if (!visible) {
        return null;
    }

    return (
        <div
            ref={menuRef}
            className="context-menu"
            onClick={(e) => e.stopPropagation()}
        >
            {items.map((item) => (
                <div key={item.key} className="context-menu-row">
                    {item.children ? (
                        <button
                            type="button"
                            className="context-menu-item has-children"
                            onClick={() => setExpandedKey((key) => key === item.key ? null : item.key)}
                            title={item.title}
                            disabled={item.disabled}
                            aria-expanded={expandedKey === item.key}
                        >
                            {item.label}
                        </button>
                    ) : (
                        <div
                            className={`context-menu-item${item.disabled ? " disabled" : ""}`}
                            onClick={item.disabled ? undefined : item.onClick}
                            title={item.title}
                        >
                            {item.label}
                        </div>
                    )}
                    {item.children && expandedKey === item.key && !item.disabled && (
                        <div ref={submenuRef} className="context-submenu">
                            {item.children.map((child) => (
                                <div
                                    key={child.key}
                                    className={`context-menu-item${child.disabled ? " disabled" : ""}`}
                                    onClick={child.disabled ? undefined : () => {
                                        child.onClick?.();
                                        onClose();
                                    }}
                                    title={child.title}
                                >
                                    {child.label}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default ContextMenu;
